import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Chip,
    Avatar,
} from '@mui/material';
import {
    Business as DeptIcon,
    Person as PersonIcon,
    Star as LeaderIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useUIStore } from '@store/uiStore';
import api from '../api/axios';

const MyDepartment = () => {
    const { user } = useAuthStore();
    const { language } = useUIStore();
    const [loading, setLoading] = useState(true);
    const [department, setDepartment] = useState(null);
    const [users, setUsers] = useState([]);
    const [error, setError] = useState('');

    const isRTL = language === 'ar';

    useEffect(() => {
        fetchDepartmentData();
    }, []);

    const fetchDepartmentData = async () => {
        try {
            setLoading(true);
            const response = await api.get('/departments/my-users');
            // axios interceptor returns response.data, so we access .data directly
            setDepartment(response.data.department);
            setUsers(response.data.users || []);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load department data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box p={3}>
            {/* Header */}
            <Box mb={4}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {isRTL ? 'قسمي' : 'My Department'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {isRTL ? 'عرض معلومات القسم والمستخدمين' : 'View department information and users'}
                </Typography>
            </Box>

            {/* Department Info Card */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #1a3a6b 0%, #2a7d62 100%)' }}>
                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                            <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'rgba(255,255,255,0.2)' }}>
                                <DeptIcon sx={{ fontSize: 40, color: 'white' }} />
                            </Avatar>
                            <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                                {isRTL ? department?.name_ar : department?.name_en}
                            </Typography>
                            {department?.name_ar && department?.name_en && (
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    {isRTL ? department?.name_en : department?.name_ar}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon color="primary" />
                                {isRTL ? 'المستخدمون' : 'Users'} ({users.length})
                            </Typography>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{
                                            '& .MuiTableCell-root': {
                                                backgroundColor: '#1a3a6b !important',
                                                color: 'white !important',
                                                fontWeight: 600
                                            }
                                        }}>
                                            <TableCell>{isRTL ? 'الاسم' : 'Name'}</TableCell>
                                            <TableCell>{isRTL ? 'البريد الإلكتروني' : 'Email'}</TableCell>
                                            <TableCell>{isRTL ? 'الدور' : 'Role'}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography color="text.secondary" py={2}>
                                                        {isRTL ? 'لا يوجد مستخدمين' : 'No users found'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            users.map((u) => (
                                                <TableRow key={u.id} hover>
                                                    <TableCell>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Avatar sx={{ width: 32, height: 32, bgcolor: u.is_department_leader ? 'primary.main' : 'grey.400' }}>
                                                                {u.name_en?.charAt(0)?.toUpperCase()}
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={500}>
                                                                    {isRTL ? u.name_ar || u.name_en : u.name_en}
                                                                </Typography>
                                                                {u.name_ar && u.name_en && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {isRTL ? u.name_en : u.name_ar}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {u.email ? (
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                <EmailIcon fontSize="small" color="action" />
                                                                <Typography variant="body2">{u.email}</Typography>
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">-</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {u.is_department_leader ? (
                                                            <Chip
                                                                icon={<LeaderIcon />}
                                                                label={isRTL ? 'مدير' : 'Leader'}
                                                                color="primary"
                                                                size="small"
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label={isRTL ? 'مستخدم' : 'User'}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default MyDepartment;
